import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip
} from '@mui/material';
import {
  Group as GroupIcon,
  CalendarToday as CalendarIcon,
  Event as EventIcon,
  Person as PersonIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useGroups } from '../contexts/GroupContext';
import { useCalendar } from '../contexts/CalendarContext';
import UserInvitations from '../components/UserInvitations';

export default function TestPage() {
  const { currentUser, updateUserProfile } = useAuth();
  const { groups, createGroup, loading: groupsLoading, sendInvitation, leaveGroup } = useGroups();
  const { 
    unavailableDates, 
    markDateUnavailable,
    getGroupMemberAvailability 
  } = useCalendar();

  const [testResults, setTestResults] = useState({});
  const [runningTests, setRunningTests] = useState(false);

  const runAllTests = async () => {
    setRunningTests(true);
    const results = {};

    try {
      // Test 1: Basic authentication
      results.auth = currentUser ? '✅ User authenticated' : '❌ No user authenticated';

      // Test 2: Groups access
      results.groups = groupsLoading ? '⏳ Loading groups...' : 
        groups.length > 0 ? `✅ ${groups.length} groups found` : '⚠️ No groups found';

      // Test 3: Calendar access
      results.calendar = unavailableDates ? `✅ Calendar has ${unavailableDates.length} unavailable dates` : '❌ Calendar not accessible';

      // Test 4: Group member availability (if groups exist)
      if (groups.length > 0 && getGroupMemberAvailability) {
        try {
          const availability = await getGroupMemberAvailability(groups[0].id);
          results.groupAvailability = availability && Object.keys(availability).length > 0 ? 
            `✅ Group availability loaded (${Object.keys(availability).length} dates)` : 
            '⚠️ Group availability empty or failed';
        } catch (error) {
          results.groupAvailability = `❌ Group availability error: ${error.message}`;
        }
      } else {
        results.groupAvailability = '⚠️ Cannot test group availability (no groups or function)';
      }

      // Test 5: Create test unavailable date
      if (currentUser) {
        try {
          const testDate = new Date();
          testDate.setDate(testDate.getDate() + 7); // Next week
          
          const result = await markDateUnavailable(testDate, 'Test unavailability for calendar integration');
          results.testDate = result ? '✅ Test unavailable date created' : '❌ Failed to create test date';
        } catch (error) {
          results.testDate = `❌ Test date error: ${error.message}`;
        }
      } else {
        results.testDate = '⚠️ Cannot test date creation (no user)';
      }

    } catch (error) {
      results.general = `❌ General test error: ${error.message}`;
    }

    setTestResults(results);
    setRunningTests(false);
  };

  const testProfileUpdate = async () => {
    if (!currentUser) return;
    
    try {
      await updateUserProfile({
        bio: `Test bio updated at ${new Date().toLocaleTimeString()}`
      });
      alert('Profile updated successfully!');
    } catch (error) {
      alert(`Profile update failed: ${error.message}`);
    }
  };

  const testGroupCreation = async () => {
    if (!currentUser) return;
    
    try {
      const testGroup = {
        name: `Test Group ${Date.now()}`,
        description: 'Test group for calendar integration testing',
        privacy: 'private'
      };
      
      const result = await createGroup(testGroup);
      alert(`Test group created successfully! ID: ${result.id}`);
    } catch (error) {
      alert(`Group creation failed: ${error.message}`);
    }
  };

  const testGroupUpdate = async () => {
    if (!currentUser || groups.length === 0) return;
    
    try {
      // This would require an updateGroup function in the context
      alert('Group update test not implemented yet');
    } catch (error) {
      alert(`Group update failed: ${error.message}`);
    }
  };

  const testGroupDeletion = async () => {
    if (!currentUser || groups.length === 0) return;
    
    try {
      // This would require a deleteGroup function in the context
      alert('Group deletion test not implemented yet');
    } catch (error) {
      alert(`Group deletion failed: ${error.message}`);
    }
  };

  const testMemberManagement = async () => {
    if (!currentUser || groups.length === 0) return;
    
    try {
      const group = groups[0];
      alert(`Member management test for group: ${group.name} (${group.memberCount} members)`);
    } catch (error) {
      alert(`Member management test failed: ${error.message}`);
    }
  };

  const testInvitationSystem = async () => {
    if (!currentUser || groups.length === 0) return;
    
    try {
      const group = groups[0];
      
      // Check if user can send invitations
      const canSendInvitations = group.createdBy === currentUser.uid || 
        group.memberRoles?.[currentUser.uid] === 'admin' ||
        group.memberRoles?.[currentUser.uid] === 'moderator';
      
      if (!canSendInvitations) {
        alert(`You don't have permission to send invitations to group "${group.name}". You need to be an admin or moderator.`);
        return;
      }
      
      // Test sending an invitation
      const testEmail = prompt(`Enter an email to send a test invitation to for group "${group.name}":`, 'test@example.com');
      if (!testEmail) return;
      
      try {
        const result = await sendInvitation(group.id, testEmail, 'This is a test invitation from the test page!');
        
        if (result.success) {
          alert(`✅ Test invitation sent successfully to ${testEmail}!\n\nInvitation ID: ${result.invitationId}\n\nYou can now test the acceptance flow by:\n1. Signing in with the invited email\n2. Going to the Dashboard to see the invitation\n3. Accepting or declining the invitation`);
        } else {
          alert(`❌ Failed to send invitation: ${result.message || 'Unknown error'}`);
        }
      } catch (error) {
        alert(`❌ Failed to send invitation: ${error.message}\n\nThis could be because:\n• User already has a pending invitation\n• User is already a member of the group\n• User has declined a previous invitation (this is allowed)`);
      }
    } catch (error) {
      alert(`❌ Invitation system test failed: ${error.message}`);
    }
  };

  const testLeaveGroup = async () => {
    if (!currentUser || groups.length === 0) return;
    
    try {
      const group = groups[0];
      
      // Check if user is a member but not the creator
      const isMember = group.members.includes(currentUser.uid);
      const isCreator = group.createdBy === currentUser.uid;
      
      if (!isMember) {
        alert(`You are not a member of group "${group.name}". You need to be a member to test leaving.`);
        return;
      }
      
      if (isCreator) {
        alert(`You are the creator of group "${group.name}". Creators cannot leave their own groups, they can only delete them.`);
        return;
      }
      
      // Test leaving the group
      if (window.confirm(`Are you sure you want to test leaving group "${group.name}"? This will remove you from the group.`)) {
        await leaveGroup(group.id);
        alert(`✅ Successfully left group "${group.name}"! You should no longer see it in your groups list.`);
      }
    } catch (error) {
      alert(`❌ Leave group test failed: ${error.message}`);
    }
  };

  const testRoleChecking = async () => {
    if (!currentUser || groups.length === 0) return;
    
    try {
      const group = groups[0];
      const isCreator = group.createdBy === currentUser.uid;
      const isAdmin = group.memberRoles?.[currentUser.uid] === 'admin';
      
      alert(`Role check for group "${group.name}": Creator: ${isCreator}, Admin: ${isAdmin}`);
    } catch (error) {
      alert(`Role checking failed: ${error.message}`);
    }
  };

  const testCleanupOrphanedGroups = async () => {
    if (!currentUser) return;
    
    try {
      // This would require a cleanupOrphanedGroups function in the context
      alert('Cleanup test not implemented yet');
    } catch (error) {
      alert(`Cleanup test failed: ${error.message}`);
    }
  };

  const testProfilePicture = () => {
    if (!currentUser) return;
    
    alert(`Profile picture test for user: ${currentUser.displayName || currentUser.email}`);
  };

  const getTestIcon = (result) => {
    if (result.includes('✅')) return <SuccessIcon color="success" />;
    if (result.includes('❌')) return <ErrorIcon color="error" />;
    if (result.includes('⚠️')) return <WarningIcon color="warning" />;
    if (result.includes('⏳')) return <InfoIcon color="info" />;
    return <InfoIcon />;
  };

  const getTestColor = (result) => {
    if (result.includes('✅')) return 'success';
    if (result.includes('❌')) return 'error';
    if (result.includes('⚠️')) return 'warning';
    if (result.includes('⏳')) return 'info';
    return 'default';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        🧪 Feature Testing Dashboard
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Test various features of the party planning app to ensure everything is working correctly.
      </Typography>

      {/* Calendar Integration Test Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon color="primary" />
          Calendar Integration Tests
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          Test the new calendar integration features including group availability and member coordination.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            onClick={runAllTests}
            disabled={runningTests}
            startIcon={<CalendarIcon />}
            sx={{ mr: 2 }}
          >
            {runningTests ? 'Running Tests...' : 'Run Calendar Integration Tests'}
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => setTestResults({})}
            disabled={runningTests}
          >
            Clear Results
          </Button>
        </Box>

        {/* Test Results */}
        {Object.keys(testResults).length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>Test Results:</Typography>
            <List dense>
              {Object.entries(testResults).map(([testName, result]) => (
                <ListItem key={testName} sx={{ px: 0 }}>
                  <ListItemIcon>
                    {getTestIcon(result)}
                  </ListItemIcon>
                  <ListItemText
                    primary={testName.charAt(0).toUpperCase() + testName.slice(1).replace(/([A-Z])/g, ' $1')}
                    secondary={result}
                  />
                  <Chip 
                    label={getTestColor(result)} 
                    size="small" 
                    color={getTestColor(result)}
                    variant="outlined"
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Calendar Integration Status */}
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h6" gutterBottom>Calendar Integration Status:</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'grey.50' }}>
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                {unavailableDates ? unavailableDates.length : 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Personal Unavailable Dates
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'grey.50' }}>
              <Typography variant="h4" color="secondary.main" fontWeight="bold">
                {groups.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Available Groups
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'grey.50' }}>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {currentUser ? '✅' : '❌'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Authentication Status
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* General Feature Tests */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon color="primary" />
          General Feature Tests
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="outlined"
              onClick={testProfileUpdate}
              fullWidth
              sx={{ height: 100, flexDirection: 'column', gap: 1 }}
            >
              <PersonIcon />
              Test Profile Update
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="outlined"
              onClick={testGroupCreation}
              fullWidth
              sx={{ height: 100, flexDirection: 'column', gap: 1 }}
            >
              <GroupIcon />
              Test Group Creation
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="outlined"
              onClick={testGroupUpdate}
              fullWidth
              sx={{ height: 100, flexDirection: 'column', gap: 1 }}
            >
              <EventIcon />
              Test Group Update
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="outlined"
              onClick={testGroupDeletion}
              fullWidth
              sx={{ height: 100, flexDirection: 'column', gap: 1 }}
            >
              <GroupIcon />
              Test Group Deletion
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="outlined"
              onClick={testMemberManagement}
              fullWidth
              sx={{ height: 100, flexDirection: 'column', gap: 1 }}
            >
              <PersonIcon />
              Test Member Management
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="outlined"
              onClick={testInvitationSystem}
              fullWidth
              sx={{ height: 100, flexDirection: 'column', gap: 1 }}
            >
              <EventIcon />
              Test Invitation System
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="outlined"
              onClick={testRoleChecking}
              fullWidth
              sx={{ height: 100, flexDirection: 'column', gap: 1 }}
            >
              <GroupIcon />
              Test Role Checking
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="outlined"
              onClick={testLeaveGroup}
              fullWidth
              sx={{ height: 100, flexDirection: 'column', gap: 1 }}
            >
              <GroupIcon />
              Test Leave Group
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="outlined"
              onClick={testCleanupOrphanedGroups}
              fullWidth
              sx={{ height: 100, flexDirection: 'column', gap: 1 }}
            >
              <GroupIcon />
              Test Cleanup
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="outlined"
              onClick={testProfilePicture}
              fullWidth
              sx={{ height: 100, flexDirection: 'column', gap: 1 }}
            >
              <PersonIcon />
              Test Profile Picture
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Invitation System Test */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon color="primary" />
          Invitation System Test
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          Test the group invitation system. Send invitations and see how users can accept/decline them.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            onClick={testInvitationSystem}
            disabled={!currentUser || groups.length === 0}
            startIcon={<GroupIcon />}
            sx={{ mr: 2 }}
          >
            Test Invitation System
          </Button>
          
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Note: You need to be an admin or moderator of a group to send invitations.
          </Typography>
        </Box>

        {/* Display current user's invitations */}
        <Typography variant="h6" gutterBottom>Your Current Invitations</Typography>
        <UserInvitations />
      </Paper>

      {/* Current Status */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>Current Status</Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>User Information</Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="User ID"
                  secondary={currentUser?.uid || 'Not authenticated'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Email"
                  secondary={currentUser?.email || 'Not available'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Display Name"
                  secondary={currentUser?.displayName || 'Not set'}
                />
              </ListItem>
            </List>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>App Status</Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Groups Count"
                  secondary={groupsLoading ? 'Loading...' : groups.length}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Unavailable Dates"
                  secondary={unavailableDates ? unavailableDates.length : 'Not loaded'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Authentication"
                  secondary={currentUser ? 'Authenticated' : 'Not authenticated'}
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
}
